import {
  ANALYSIS_RECIPE_SCHEMA_VERSION,
  type AnalysisJsonObject,
  type AnalysisJsonValue,
  type AnalysisRecipeChart,
  type AnalysisRecipeControl,
  type AnalysisRecipeSource,
  type AnalysisRecipeState,
  type AnalysisRecipeStep,
  type AnalysisSheetRecipe,
} from '@/types/analysisRecipe.types';
import { mapAnalysisChartColumnReferences } from '@/helpers/analysisChartReferences.helpers';
import { analysisStepOutputField } from '@/helpers/analysisMadeStep.helpers';
import { MAX_DISPLAY_DECIMALS } from '@/helpers/analysisColumnDisplay.helpers';

const INVALID_STATE_MESSAGE = 'Invalid analysis recipe state';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === 'string');

const isScalar = (value: unknown): value is string | number | boolean | null =>
  value === null ||
  typeof value === 'string' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  typeof value === 'boolean';

const isJsonValue = (value: unknown): value is AnalysisJsonValue => {
  if (isScalar(value)) return true;
  if (Array.isArray(value)) return value.every(isJsonValue);
  return isRecord(value) && Object.values(value).every(isJsonValue);
};

const isJsonObject = (value: unknown): value is AnalysisJsonObject =>
  isRecord(value) && Object.values(value).every(isJsonValue);

const isSearchSource = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  if (typeof value.query !== 'string' || !value.query) return false;
  if (value.mode !== 'keyword' && value.mode !== 'semantic') return false;
  if (typeof value.contains !== 'boolean') return false;
  return (
    value.filters === undefined ||
    (Array.isArray(value.filters) && value.filters.every(isJsonObject))
  );
};

const isSource = (value: unknown): value is AnalysisRecipeSource => {
  if (!isRecord(value)) return false;
  if (value.kind !== 'label' && value.kind !== 'exploration') return false;
  if (value.label !== undefined && typeof value.label !== 'string')
    return false;
  if (value.kind === 'label' && !value.label) return false;
  if (value.search !== undefined && !isSearchSource(value.search)) return false;
  return value.cohort === undefined || isJsonObject(value.cohort);
};

const isStep = (value: unknown): value is AnalysisRecipeStep => {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    (value.kind === 'lookup' ||
      value.kind === 'rollup' ||
      value.kind === 'join' ||
      value.kind === 'calc') &&
    typeof value.sourceLabel === 'string' &&
    isStringArray(value.outputColumns) &&
    typeof value.sentence === 'string' &&
    isJsonObject(value.config)
  );
};

const isControl = (value: unknown): value is AnalysisRecipeControl => {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === 'string' &&
    typeof value.paramName === 'string' &&
    typeof value.field === 'string' &&
    typeof value.operator === 'string' &&
    isScalar(value.value) &&
    typeof value.applied === 'boolean'
  );
};

const isChart = (value: unknown): value is AnalysisRecipeChart => {
  if (!isRecord(value)) return false;
  const ownParamsValid =
    value.ownParams === undefined ||
    (isRecord(value.ownParams) &&
      Object.values(value.ownParams).every(isScalar));
  return (
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.visualization === 'string' &&
    isJsonObject(value.config) &&
    ownParamsValid
  );
};

const isBreakdownAggregation = (aggregation: unknown): boolean =>
  isRecord(aggregation) &&
  typeof aggregation.id === 'string' &&
  (aggregation.field === undefined || typeof aggregation.field === 'string') &&
  ['count', 'sum', 'avg', 'min', 'max'].includes(String(aggregation.op)) &&
  typeof aggregation.outputColumn === 'string';

const isSheet = (value: unknown): value is AnalysisSheetRecipe => {
  if (!isRecord(value)) return false;
  const breakdownAggregationsValid =
    !isRecord(value.breakdown) ||
    value.breakdown.aggregations === undefined ||
    (Array.isArray(value.breakdown.aggregations) &&
      value.breakdown.aggregations.every(isBreakdownAggregation));
  const removedBreakdownAggregationsValid =
    !isRecord(value.breakdown) ||
    value.breakdown.removedAggregations === undefined ||
    (Array.isArray(value.breakdown.removedAggregations) &&
      value.breakdown.removedAggregations.every(isBreakdownAggregation));
  const breakdownWindowsValid =
    !isRecord(value.breakdown) ||
    value.breakdown.windows === undefined ||
    (Array.isArray(value.breakdown.windows) &&
      value.breakdown.windows.every(
        (window) =>
          isRecord(window) &&
          typeof window.id === 'string' &&
          (window.op === 'percent_of_total' || window.op === 'rank') &&
          typeof window.sourceColumn === 'string' &&
          typeof window.outputColumn === 'string',
      ));
  const breakdownValid =
    value.breakdown === null ||
    (isRecord(value.breakdown) &&
      typeof value.breakdown.parentSheetId === 'string' &&
      // Optional: a total sheet has no group key at all (see
      // `isAnalysisTotalSheet`). Requiring a string here rejected the whole
      // recipe — on serialize as well as deserialize — so a view containing a
      // total sheet could not be saved or restored.
      (value.breakdown.groupByColumn === undefined ||
        typeof value.breakdown.groupByColumn === 'string') &&
      (value.breakdown.status === undefined ||
        value.breakdown.status === 'live' ||
        value.breakdown.status === 'stale' ||
        value.breakdown.status === 'broken') &&
      (value.breakdown.brokenReason === undefined ||
        typeof value.breakdown.brokenReason === 'string') &&
      breakdownAggregationsValid &&
      removedBreakdownAggregationsValid &&
      breakdownWindowsValid);
  // Optional, and absent on every recipe written before it existed — so
  // `undefined` has to pass. A present-but-malformed map is rejected instead of
  // being carried into the formatter, where a non-numeric value would reach
  // `toFixed` and throw inside a cell.
  const columnPrecisionValid =
    value.columnPrecision === undefined ||
    (isRecord(value.columnPrecision) &&
      Object.values(value.columnPrecision).every(
        (decimals) => typeof decimals === 'number' && Number.isFinite(decimals),
      ));
  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    (value.face === 'data' || value.face === 'analysis') &&
    isSource(value.source) &&
    (value.worksetRootSheetId === undefined ||
      typeof value.worksetRootSheetId === 'string') &&
    Array.isArray(value.steps) &&
    value.steps.every(isStep) &&
    (value.keepFilter === null || isJsonObject(value.keepFilter)) &&
    Array.isArray(value.controls) &&
    value.controls.every(isControl) &&
    breakdownValid &&
    columnPrecisionValid &&
    Array.isArray(value.charts) &&
    value.charts.every(isChart)
  );
};

const isState = (value: unknown): value is AnalysisRecipeState => {
  if (!isRecord(value)) return false;
  if (value.schemaVersion !== ANALYSIS_RECIPE_SCHEMA_VERSION) return false;
  if (value.activeSheetId !== null && typeof value.activeSheetId !== 'string') {
    return false;
  }
  if (!isStringArray(value.sheetOrder) || !isRecord(value.sheets)) return false;

  const sheetEntries = Object.entries(value.sheets);
  if (
    !sheetEntries.every(
      ([sheetId, sheet]) => isSheet(sheet) && sheet.id === sheetId,
    )
  ) {
    return false;
  }
  const sheets = value.sheets as Record<string, AnalysisSheetRecipe>;

  const sheetIds = new Set(sheetEntries.map(([sheetId]) => sheetId));
  const orderedIds = new Set(value.sheetOrder);
  if (
    orderedIds.size !== value.sheetOrder.length ||
    orderedIds.size !== sheetIds.size ||
    value.sheetOrder.some((sheetId) => !sheetIds.has(sheetId))
  ) {
    return false;
  }
  if (value.activeSheetId !== null && !sheetIds.has(value.activeSheetId)) {
    return false;
  }

  return Object.values(sheets).every((sheet) => {
    const parentId = sheet.breakdown?.parentSheetId;
    const worksetRootId = sheet.worksetRootSheetId;
    const parentValid =
      parentId === undefined ||
      (parentId !== sheet.id && sheetIds.has(parentId));
    const worksetValid =
      worksetRootId === undefined ||
      (worksetRootId !== sheet.id && sheetIds.has(worksetRootId));
    return parentValid && worksetValid;
  });
};

const cloneState = (state: AnalysisRecipeState): AnalysisRecipeState =>
  JSON.parse(JSON.stringify(state)) as AnalysisRecipeState;

const legacyOutputReplacement = (
  field: string,
  producerId: string,
): string | null => {
  const prefix = `analysis:${producerId}:`;
  return field.startsWith(prefix)
    ? analysisStepOutputField(producerId, field.slice(prefix.length))
    : null;
};

const analysisFieldMigrationMap = (
  state: AnalysisRecipeState,
): Map<string, string> => {
  const replacements = new Map<string, string>();
  Object.values(state.sheets).forEach((sheet) => {
    sheet.steps.forEach((step) => {
      step.outputColumns.forEach((field) => {
        const replacement = legacyOutputReplacement(field, step.id);
        if (replacement) replacements.set(field, replacement);
      });
    });
    const breakdown = sheet.breakdown;
    if (!breakdown) return;
    [
      ...(breakdown.aggregations ?? []),
      ...(breakdown.removedAggregations ?? []),
    ].forEach((aggregation) => {
      const replacement = legacyOutputReplacement(
        aggregation.outputColumn,
        sheet.id,
      );
      if (replacement) {
        replacements.set(aggregation.outputColumn, replacement);
      }
    });
    (breakdown.windows ?? []).forEach((window) => {
      const replacement = legacyOutputReplacement(
        window.outputColumn,
        sheet.id,
      );
      if (replacement) replacements.set(window.outputColumn, replacement);
    });
  });
  return replacements;
};

const REFERENCE_KEYS = new Set([
  'field',
  'sourceColumn',
  'groupByColumn',
  'outputColumn',
  'column',
  'columnKey',
  'xAxis',
  'category',
  'valueColumn',
]);

const migrateJsonReferences = (
  value: AnalysisJsonValue,
  replacements: Map<string, string>,
  parent?: AnalysisJsonObject,
  key?: string,
): AnalysisJsonValue => {
  if (Array.isArray(value)) {
    return value.map((entry) =>
      migrateJsonReferences(entry, replacements, parent, key),
    );
  }
  if (!value || typeof value !== 'object') {
    if (
      typeof value === 'string' &&
      (REFERENCE_KEYS.has(key ?? '') ||
        (key === 'value' && parent?.kind === 'column') ||
        key === 'yAxisColumns')
    ) {
      return replacements.get(value) ?? value;
    }
    return value;
  }
  const object = value as AnalysisJsonObject;
  return Object.fromEntries(
    Object.entries(object).map(([childKey, child]) => [
      childKey,
      migrateJsonReferences(child, replacements, object, childKey),
    ]),
  );
};

/**
 * Rewrites every reference to a renamed output column within one sheet.
 *
 * A calc step's output key encodes its *name* as well as its step id
 * (`analysisStepOutputField`), so renaming a calculated column mints a new key
 * and silently orphans anything pointing at the old one — downstream formulas,
 * Kept filters, promoted controls, breakdown metrics and chart axes. Extracted
 * from the legacy migration below, which already had to solve exactly this,
 * so a rename and a schema migration cannot drift apart.
 *
 * Mutates the sheet in place; callers pass a clone.
 */
export function applyAnalysisFieldReplacements(
  sheet: AnalysisSheetRecipe,
  replacements: Map<string, string>,
): void {
  if (replacements.size === 0) return;
  sheet.steps.forEach((step) => {
    step.outputColumns = step.outputColumns.map(
      (field) => replacements.get(field) ?? field,
    );
    step.config = migrateJsonReferences(
      step.config,
      replacements,
    ) as AnalysisJsonObject;
  });
  if (sheet.keepFilter) {
    sheet.keepFilter = migrateJsonReferences(
      sheet.keepFilter,
      replacements,
    ) as AnalysisJsonObject;
  }
  sheet.controls.forEach((control) => {
    control.field = replacements.get(control.field) ?? control.field;
  });
  // `analysisStepOutputField` encodes the column *name*, so a rename mints a new
  // key. Without this the chosen precision stays keyed to the old one: it stops
  // applying, and it never goes away either.
  if (sheet.columnPrecision) {
    sheet.columnPrecision = Object.fromEntries(
      Object.entries(sheet.columnPrecision).map(([column, decimals]) => [
        replacements.get(column) ?? column,
        decimals,
      ]),
    );
  }
  if (sheet.breakdown) {
    const breakdown = sheet.breakdown;
    if (breakdown.groupByColumn !== undefined) {
      breakdown.groupByColumn =
        replacements.get(breakdown.groupByColumn) ?? breakdown.groupByColumn;
    }
    [
      ...(breakdown.aggregations ?? []),
      ...(breakdown.removedAggregations ?? []),
    ].forEach((aggregation) => {
      if (aggregation.field) {
        aggregation.field =
          replacements.get(aggregation.field) ?? aggregation.field;
      }
      aggregation.outputColumn =
        replacements.get(aggregation.outputColumn) ?? aggregation.outputColumn;
    });
    (breakdown.windows ?? []).forEach((window) => {
      window.sourceColumn =
        replacements.get(window.sourceColumn) ?? window.sourceColumn;
      window.outputColumn =
        replacements.get(window.outputColumn) ?? window.outputColumn;
    });
  }
  sheet.charts.forEach((chart) => {
    chart.config = mapAnalysisChartColumnReferences(
      chart.config,
      (column) => replacements.get(column) ?? column,
    );
  });
}

const normalizeAnalysisRecipeState = (
  state: AnalysisRecipeState,
): AnalysisRecipeState => {
  const normalized = cloneState(state);
  const replacements = analysisFieldMigrationMap(normalized);
  normalized.schemaVersion = ANALYSIS_RECIPE_SCHEMA_VERSION;
  Object.values(normalized.sheets).forEach((sheet) => {
    applyAnalysisFieldReplacements(sheet, replacements);
  });
  return normalized;
};

export function createEmptyAnalysisRecipeState(): AnalysisRecipeState {
  return {
    schemaVersion: ANALYSIS_RECIPE_SCHEMA_VERSION,
    activeSheetId: null,
    sheetOrder: [],
    sheets: {},
  };
}

export function serializeAnalysisRecipeState(
  state: AnalysisRecipeState,
): string {
  if (!isState(state)) {
    throw new Error(INVALID_STATE_MESSAGE);
  }
  return JSON.stringify(normalizeAnalysisRecipeState(state));
}

export function deserializeAnalysisRecipeState(
  value: string | unknown,
): AnalysisRecipeState {
  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      throw new Error(INVALID_STATE_MESSAGE);
    }
  }

  if (
    isRecord(parsed) &&
    typeof parsed.schemaVersion === 'number' &&
    parsed.schemaVersion !== 1 &&
    parsed.schemaVersion !== ANALYSIS_RECIPE_SCHEMA_VERSION
  ) {
    throw new Error(
      `Unsupported analysis recipe schema version: ${parsed.schemaVersion}`,
    );
  }
  const normalized =
    isRecord(parsed) &&
    isRecord(parsed.sheets) &&
    (parsed.schemaVersion === 1 ||
      parsed.schemaVersion === ANALYSIS_RECIPE_SCHEMA_VERSION)
      ? normalizeAnalysisRecipeState(parsed as unknown as AnalysisRecipeState)
      : parsed;
  if (!isState(normalized)) {
    throw new Error(INVALID_STATE_MESSAGE);
  }
  return cloneState(normalized);
}

/**
 * Sets, or clears, a column's displayed decimal places on a sheet.
 *
 * `null` removes the entry rather than writing a zero: absent means "use the
 * format's default", and 0 means "show whole numbers", which are different
 * answers. Storing 0 for a cleared control would silently turn every percentage
 * into `83%`.
 *
 * Display only — the recipe's steps, filters and aggregations are untouched, so
 * nothing here changes what the pipeline computes or what it returns.
 */
export function setAnalysisColumnPrecision(
  sheet: AnalysisSheetRecipe,
  column: string,
  decimals: number | null,
): AnalysisSheetRecipe {
  const current = sheet.columnPrecision ?? {};
  if (decimals === null) {
    if (!(column in current)) return sheet;
    const next = { ...current };
    delete next[column];
    if (Object.keys(next).length > 0) {
      return { ...sheet, columnPrecision: next };
    }
    // Drop the key entirely rather than leaving an empty map behind, so a sheet
    // with no precision set serializes exactly as it did before this existed.
    const cleared = { ...sheet };
    delete cleared.columnPrecision;
    return cleared;
  }
  if (!Number.isFinite(decimals)) return sheet;
  const clamped = Math.min(
    Math.max(Math.trunc(decimals), 0),
    MAX_DISPLAY_DECIMALS,
  );
  if (current[column] === clamped) return sheet;
  return {
    ...sheet,
    columnPrecision: { ...current, [column]: clamped },
  };
}
